import React, { useEffect, useMemo, useState } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import {
  FaChevronCircleLeft,
  FaChevronCircleRight,
  FaPlus,
  FaSearch,
} from "react-icons/fa"
import {
  Card,
  CardBody,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Row,
  Badge,
} from "reactstrap"
import { PiHandTapFill } from "react-icons/pi"
import Swal from "sweetalert2"
import moment from "moment"
import Select from "react-select"
import { DatePicker } from "antd"
import { fetchData, FetchPostData } from "utils/Common_api_functions"
import { formatAnyTime } from "utils/commonFunctions"
import Loader from "components/Loader/loader"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"

const { RangePicker } = DatePicker

const calculateHours = (inTime, outTime) => {
  if (!inTime || !outTime) return 0

  const [inHours, inMinutes] = inTime.split(":").map(Number)
  const [outHours, outMinutes] = outTime.split(":").map(Number)

  let inDate = new Date(0, 0, 0, inHours, inMinutes)
  let outDate = new Date(0, 0, 0, outHours, outMinutes)

  if (outDate <= inDate) {
    outDate.setDate(outDate.getDate() + 1)
  }

  const diff = (outDate - inDate) / (1000 * 60 * 60)
  return diff.toFixed(2)
}

const computeLeaveBalanceAfterSelection = (baseBalance, selectedCount) => {
  if (!baseBalance || selectedCount === 0) {
    return baseBalance
      ? {
          ...baseBalance,
          displayPaid: baseBalance.takenLeaves,
          displayExtra: baseBalance.extraLeaves,
          displayRemaining: baseBalance.remainingLeaves,
          selectedPaid: 0,
          selectedExtra: 0,
        }
      : null
  }

  const baseRemaining = Math.max(0, Number(baseBalance.remainingLeaves) || 0)
  const selectedPaid = Math.min(selectedCount, baseRemaining)
  const selectedExtra = Math.max(0, selectedCount - baseRemaining)

  return {
    ...baseBalance,
    displayPaid: Number(baseBalance.takenLeaves) + selectedPaid,
    displayExtra: Number(baseBalance.extraLeaves) + selectedExtra,
    displayRemaining: Math.max(0, baseRemaining - selectedPaid),
    selectedPaid,
    selectedExtra,
  }
}

const Leaves = () => {
  const [dateRange, setDateRange] = useState([])
  const [leavedateRange, setLeaveDateRange] = useState([])
  const [perPageItem, setPerPageItem] = useState(10)
  const [searchString, setSearchString] = useState("")
  const [currentPage, setCurrentPage] = useState(1)

  const [leaves, setLeaves] = useState([])
  const [allleaves, setallLeaves] = useState([])

  const [employees, setemployees] = useState([])
  const [jobs, setjobs] = useState([])

  // State for modals
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedLeave, setSelectedLeave] = useState(null)
  const [addModalOpen, setAddModalOpen] = useState(false)

  // New leave state
  const [newLeave, setNewLeave] = useState({
    employeeId: "",
    fromDate: "",
    toDate: "",
    calendarSlotIds: "",
    reason: "",
  })

  // Bulk selection states
  const [selectedLeaves, setSelectedLeaves] = useState([])
  const [bulkAction, setBulkAction] = useState()

  const [loading, setLoading] = useState(false)

  const [validationErrors, setValidationErrors] = useState({})

  const [leaveBalanceInfo, setLeaveBalanceInfo] = useState(null)

  const resetAddLeaveForm = () => {
    setNewLeave({ employeeId: "", calendarSlotIds: [], reason: "" })
    setLeaveDateRange([])
    setjobs([])
    setValidationErrors({})
    setLeaveBalanceInfo(null)
  }

  useEffect(() => {
    getAllLeaves()
    getEmployees()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchString])

  useEffect(() => {
    if (newLeave?.employeeId && leavedateRange !== null) {
      getEmployeeLocations()
    }
  }, [leavedateRange, newLeave?.employeeId])

  useEffect(() => {
    if (newLeave?.employeeId) {
      const empId = Number(newLeave.employeeId)

      if (jobs.length > 0) {
        const locationId = jobs[0]?.locationId
        const locationName = jobs[0]?.location?.name || ""
        const leaveSlot = Number(jobs[0]?.location?.leaveSlot || 0)

        const matchLeaves = leaves.filter(
          l =>
            Number(l.employeeId) === empId &&
            Number(l.locationId) === locationId &&
            l.status === true
        )

        const takenLeaves = matchLeaves.filter(
          l => l.isLeaveWithoutPay === false
        ).length
        const extraLeaves = matchLeaves.filter(
          l => l.isLeaveWithoutPay === true
        ).length

        setLeaveBalanceInfo({
          totalLeaves: leaveSlot,
          takenLeaves,
          extraLeaves,
          remainingLeaves: Math.max(0, leaveSlot - takenLeaves),
          locationName,
        })
      } else {
        setLeaveBalanceInfo(null)
      }
    } else {
      setLeaveBalanceInfo(null)
    }
  }, [jobs, newLeave?.employeeId, leaves])

  const displayBalance = useMemo(
    () =>
      computeLeaveBalanceAfterSelection(
        leaveBalanceInfo,
        newLeave.calendarSlotIds?.length || 0
      ),
    [leaveBalanceInfo, newLeave.calendarSlotIds]
  )

  async function getAllLeaves() {
    const APIData = await fetchData(setLoading, "holiday")
    if (APIData.success) {
      setLeaves(APIData?.data)
      setallLeaves([...APIData?.data].reverse())
    }
  }

  async function getEmployees() {
    const APIData = await fetchData(setLoading, "employee")
    if (APIData.success) {
      setemployees(APIData?.data)
    }
  }

  async function getEmployeeLocations() {
    const APIdata = await FetchPostData(
      setLoading,
      "calendar/events/by/criteria",
      {
        employeeId: Number(newLeave?.employeeId),
        fromDate: leavedateRange?.[0]?.format()?.split("T")[0],
        toDate: leavedateRange?.[1]?.format()?.split("T")[0],
      }
    )
    if (APIdata?.data?.success) {
      setjobs(APIdata?.data?.data)
    }
  }

  const filterByDate = dates => {
    if (!dates || dates.length !== 2) {
      setLeaves(allleaves)
      return
    }

    // Convert dayjs objects to moment
    const [startDayjs, endDayjs] = dates
    const start = moment(startDayjs.format("YYYY-MM-DD"), "YYYY-MM-DD")
    const end = moment(endDayjs.format("YYYY-MM-DD"), "YYYY-MM-DD")

    const filtered = allleaves.filter(l => {
      const leaveFrom = moment(l.fromDate, "YYYY-MM-DD")
      const leaveTo = moment(l.toDate, "YYYY-MM-DD")
      return (
        leaveFrom.isSameOrBefore(end, "day") &&
        leaveTo.isSameOrAfter(start, "day")
      )
    })

    setLeaves(filtered)
  }

  // --- MODAL HANDLERS ---
  const toggleApprovalModal = leave => {
    setSelectedLeave(leave)
    setModalOpen(!modalOpen)
  }

  const handleApproval = async (leaveID, status) => {
    const APIdata = await FetchPostData(setLoading, "holiday/approval/status", {
      holidayIds: [leaveID],
      status,
    })
    if (APIdata?.data?.success) {
      Swal.fire({
        title: "Success",
        text: `Holiday ${
          status === true ? "Approved" : "Rejected"
        } Successfully.`,
        icon: "success",
      })
      setModalOpen()
      getAllLeaves()
    } else {
      Swal.fire({
        title: "Something Went Wrong",
        text: APIdata?.response?.data?.message,
        icon: "error",
      })
    }
  }

  // --- ADD LEAVE ---
  const handleAddLeave = async () => {
    const errors = {}

    // Always required
    if (!newLeave.employeeId) {
      errors.employeeId = "Please select an employee"
    }

    if (
      !leavedateRange ||
      leavedateRange.length !== 2 ||
      !leavedateRange[0] ||
      !leavedateRange[1]
    ) {
      errors.dateRange = "Please select both start and end dates"
    }

    if (!newLeave.reason?.trim()) {
      errors.reason = "Please provide a reason for the holiday"
    }

    // Conditional validation for calendarSlotIds
    if (jobs.length > 0) {
      // There are scheduled shifts → must select at least one
      if (!newLeave.calendarSlotIds || newLeave.calendarSlotIds.length === 0) {
        errors.calendarSlotIds =
          "Please select at least one shift to mark as holiday"
      }

      // Check for duplicate slots already having a holiday request
      const selectedSlotIds = newLeave.calendarSlotIds.map(s => Number(s.value))
      const empId = Number(newLeave.employeeId)
      const dateFrom = leavedateRange?.[0]?.format?.("YYYY-MM-DD")
      const dateTo = leavedateRange?.[1]?.format?.("YYYY-MM-DD")
      const duplicateSlots = selectedSlotIds.filter(slotId =>
        leaves.some(l => {
          if (Number(l.employeeId) !== empId) return false
          if (l.status === false) return false
          if (l.status === null) return false
          if (Number(l.calendarSlotId) === slotId) return true
          if (l.fromDate && dateFrom && l.fromDate === dateFrom) return true
          return false
        })
      )
      if (duplicateSlots.length > 0) {
        errors.calendarSlotIds =
          "A holiday request already exists for one or more selected shifts"
      }
    }
    // If jobs.length === 0 → no validation needed (free day leave allowed)
    // But still check date-based duplicates
    if (jobs.length === 0 && newLeave.employeeId && leavedateRange) {
      const empId = Number(newLeave.employeeId)
      const dateFrom = leavedateRange?.[0]?.format?.("YYYY-MM-DD")
      const dateTo = leavedateRange?.[1]?.format?.("YYYY-MM-DD")
      const dateDuplicate = leaves.some(l => {
        if (Number(l.employeeId) !== empId) return false
        if (l.status === false) return false
        if (l.status === null) return false
        if (l.fromDate && dateFrom && l.fromDate === dateFrom) return true
        return false
      })
      if (dateDuplicate) {
        errors.dateRange = "A holiday request already exists for this date"
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      Swal.fire({
        icon: "warning",
        title: "Missing or Invalid Fields",
        html: Object.values(errors)
          .map(err => `<strong>• ${err}</strong>`)
          .join("<br/>"),
      })
      return
    }

    // Clear errors and proceed
    setValidationErrors({})

    const payload = {
      employeeId: Number(newLeave.employeeId),
      fromDate: leavedateRange[0].format("YYYY-MM-DD"),
      toDate: leavedateRange[1].format("YYYY-MM-DD"),
      calendarSlotIds:
        jobs.length > 0 && newLeave.calendarSlotIds.length > 0
          ? JSON.stringify(newLeave.calendarSlotIds.map(item => item.value))
          : JSON.stringify([]), // Send empty array if no shifts selected (or none available)
      reason: newLeave.reason.trim(),
    }

    const APIdata = await FetchPostData(setLoading, "holiday", payload)

    if (APIdata?.data?.success) {
      Swal.fire({
        icon: "success",
        title: "Holiday Request Submitted!",
        text: "The leave has been successfully applied.",
        timer: 2000,
      })

      // Reset form
      resetAddLeaveForm()
      setAddModalOpen(false)
      getAllLeaves()
    } else {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: APIdata?.response?.data?.message || "Please try again.",
      })
    }
  }

  // --- BULK APPROVAL ---
  const handleBulkAction = async () => {
    if (!bulkAction) {
      Swal.fire("Select Action", "Please select Approve or Reject.", "warning")
      return
    }

    if (selectedLeaves.length === 0) {
      Swal.fire(
        "No Leaves Selected",
        "Please select at least one leave.",
        "info"
      )
      return
    }

    const result = await Swal.fire({
      title: `Confirm to ${bulkAction === "true" ? "Approve" : "Reject"}?`,
      text: `You are about to ${bulkAction === "true" ? "Approve" : "Reject"} ${
        selectedLeaves.length
      } leaves.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, confirm",
      cancelButtonText: "Cancel",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPostData(
        setLoading,
        "holiday/approval/status",
        {
          holidayIds: selectedLeaves,
          status: bulkAction === "true",
        }
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Success",
          text: `Holidays ${
            bulkAction === "true" ? "Approved" : "Rejected"
          } Successfully.`,
          icon: "success",
        })
        setModalOpen()
        getAllLeaves()
      } else {
        Swal.fire({
          title: "Something Went Wrong",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  // --- SELECTION HANDLERS ---
  const toggleSelect = id => {
    setSelectedLeaves(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = e => {
    if (e.target.checked) {
      const allIds = filteredLeaves.map(l => l.id)
      setSelectedLeaves(allIds)
    } else {
      setSelectedLeaves([])
    }
  }

  // --- FILTER LEAVES ---
  const filteredLeaves = leaves.filter(l =>
    l.employee?.name.toLowerCase().includes(searchString.toLowerCase())
  )

  const totalPages = Math.ceil(filteredLeaves.length / perPageItem)

  const paginatedLeave = useMemo(() => {
    const start = (currentPage - 1) * perPageItem
    return filteredLeaves.slice(start, start + perPageItem)
  }, [filteredLeaves, currentPage, perPageItem])

  const { checkAccess } = usePermissionData()

  const [reassignModalOpen, setReassignModalOpen] = useState(false)
  const [reassignLeave, setReassignLeave] = useState(null)
  const [newEmployeeId, setNewEmployeeId] = useState("")

  const [releatedEmployees, setRelatedEmployees] = useState([])

  async function getLocationEmployees(day, inTime, outTime, locationId, date) {
    const APIdata = await FetchPostData(
      setLoading,
      `slot/available/employees`,
      {
        day,
        inTime,
        outTime,
        locationId,
        date,
      }
    )
    if (APIdata?.data?.success) {
      setRelatedEmployees(APIdata?.data?.employees)
    }
  }

  const openReassignModal = async leave => {
    console.log(leave)
    const date = leave?.events?.[0]?.date
    const dayNumber = moment(date, "YYYY-MM-DD").day()
    await getLocationEmployees(
      dayNumber,
      leave?.events?.[0]?.inTime,
      leave?.events?.[0]?.outTime,
      leave?.events?.[0]?.location?.id,
      date
    )

    setReassignLeave(leave)
    setNewEmployeeId("")
    setReassignModalOpen(true)
  }

  const handleReassign = async () => {
    if (!newEmployeeId) {
      Swal.fire("Select Employee", "Please select an employee.", "warning")
      return
    }

    if (String(newEmployeeId) === String(reassignLeave?.employeeId)) {
      Swal.fire(
        "Invalid Selection",
        "Cannot assign the same employee as proxy. Please select a different employee.",
        "warning"
      )
      return
    }

    // 🔥 Confirmation popup
    const confirm = await Swal.fire({
      title: "Confirm Update?",
      text: "Are you sure you want to update this employee's details?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    })

    if (!confirm.isConfirmed) return // user cancelled

    if (!reassignLeave?.employeeId) {
      Swal.fire("Error", "Employee ID is missing. Please reselect the leave.", "error")
      return
    }

    const payload = {
      employeeId: Number(reassignLeave.employeeId),
      newHourlyRate: reassignLeave?.events?.[0]?.location?.employeePrice,
      calendarSlotId: reassignLeave?.calendarSlotId,
      replaced: true,
      replacedBy: Number(newEmployeeId),
    }

    const result = await FetchPostData(
      setLoading,
      "calendar/assign/proxy/employee",
      payload
    )

    if (result?.data?.success) {
      Swal.fire("Updated!", "Employee Proxy assigned successfully", "success")
      setReassignModalOpen(false)
      getAllLeaves()
    } else {
      Swal.fire(
        "Error",
        result?.response?.data?.message || "Failed to update",
        "error"
      )
    }
  }

  return (
    <div className="page-content">
      <PermittedComponent isAccess={checkAccess(4, 1) || checkAccess(4, 2)}>
        {loading && <Loader />}
        <Breadcrumb
          title="Dashboard"
          breadcrumbItem="Holiday Management"
          description="Manage the Holiday for Employees from Here."
        />
        <Card className="shadow-lg">
          <CardBody>
            {/* --- Top Controls --- */}
            <div className="d-flex justify-content-between mb-3 flex-wrap gap-2">
              <div>
                {checkAccess(4, 4) && (
                  <div className="d-flex gap-3 align-items-center flex-wrap">
                    {selectedLeaves.length > 0 && (
                      <>
                        <select
                          className="form-select"
                          style={{ width: 150 }}
                          value={bulkAction}
                          onChange={e => setBulkAction(e.target.value)}
                        >
                          <option value="">Select Action</option>
                          <option value={true}>Approve</option>
                          <option value={false}>Reject</option>
                        </select>
                        <Button color="primary" onClick={handleBulkAction}>
                          Confirm
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="d-flex gap-3 flex-wrap align-items-center">
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <RangePicker
                    value={dateRange}
                    onChange={dates => {
                      setDateRange(dates)
                      if (!dates || dates.length === 0) {
                        // Filter cleared, reset list
                        setLeaves(allleaves)
                      } else if (dates.length === 2) {
                        filterByDate(dates)
                      }
                    }}
                    format="DD/MM/YYYY"
                    allowClear
                    className="rounded-3"
                  />
                </div>
                <div className="position-relative" style={{ maxWidth: 250 }}>
                  <input
                    className="form-control rounded-2 px-3 pe-5"
                    placeholder="Search Employee"
                    value={searchString}
                    onChange={e => setSearchString(e.target.value)}
                  />
                  <span className="position-absolute end-0 top-50 translate-middle-y pe-3 text-muted">
                    <FaSearch />
                  </span>
                </div>
                {checkAccess(4, 3) && (
                  <Button
                    className="btn btn-blue"
                    onClick={() => {
                      resetAddLeaveForm()
                      setAddModalOpen(true)
                    }}
                  >
                    <FaPlus className="me-2" /> Holiday
                  </Button>
                )}
              </div>
            </div>

            {/* --- Leaves Table --- */}
            <Table responsive>
              <thead className="bg-blue">
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      onChange={toggleSelectAll}
                      checked={
                        filteredLeaves.length > 0 &&
                        selectedLeaves.length === filteredLeaves.length
                      }
                    />
                  </th>
                  <th>#</th>
                  <th>Employee Name</th>
                  <th>Applied On</th>
                  <th>Holiday Date</th>
                  <th>Location</th>
                  <th>Total Leaves</th>
                  <th>Taken Leaves</th>
                  <th>Extra Leaves</th>
                  <th>Remaining Leaves</th>
                  <th>Time</th>
                  <th>Total Hrs</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>Reassign</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLeave.length ? (
                  paginatedLeave.map((leave, index) => (
                    <tr key={leave.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedLeaves.includes(leave.id)}
                          onChange={() => toggleSelect(leave.id)}
                        />
                      </td>
                      <td>{(currentPage - 1) * perPageItem + index + 1}</td>
                      <td className="d-flex align-items-center">
                        <img
                          src={
                            leave?.employee?.profilePicture
                              ? `${process.env.REACT_APP_IMAGE_PATH}${leave?.employee?.profilePicture}`
                              : "https://ui-avatars.com/api/?name=" +
                                encodeURIComponent(
                                  leave?.employee?.name || "User"
                                ) +
                                "&background=random"
                          }
                          alt={leave?.employee?.name}
                          className="rounded-circle me-2"
                          width="35"
                          height="35"
                          style={{ objectFit: "cover" }}
                        />
                        {leave?.employee?.name}
                      </td>
                      <td>{moment(leave.createdAt).format("DD/MM/YYYY")}</td>
                      <td>
                        {moment(leave.fromDate).format("DD/MM/YYYY")}
                        {"  "}-{"  "}
                        {moment(leave.toDate).format("DD/MM/YYYY")}
                      </td>
                      <td>{leave?.events?.[0]?.location?.name}</td>

                      <td>{Number(leave?.totalLeaves) || 0}</td>
                      <td>{Number(leave?.takenLeaves) || 0}</td>
                      <td>
                        {Number(leave?.extraLeaves) > 0 ? (
                          <span className="badge bg-warning text-dark">
                            {leave.extraLeaves}
                          </span>
                        ) : (
                          0
                        )}
                      </td>
                      <td>{Number(leave?.remainingLeaves) || 0}</td>

                      <td>
                        {`${formatAnyTime(
                          leave?.events?.[0]?.inTime
                        )} - ${formatAnyTime(leave?.events?.[0]?.outTime)}`}
                      </td>
                      <td>
                        {calculateHours(
                          leave?.events?.[0]?.inTime,
                          leave?.events?.[0]?.outTime
                        )}{" "}
                        Hrs
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge px-2 py-2 ${
                            leave.status === true
                              ? "bg-success"
                              : leave.status === false
                              ? "bg-danger"
                              : "bg-warning"
                          }`}
                        >
                          {leave?.status === null
                            ? "Pending"
                            : leave?.status === true
                            ? "Approved"
                            : "Rejected"}
                        </span>
                        {leave?.isLeaveWithoutPay === true && (
                          <span className="badge bg-warning text-dark ms-1">
                            Extra
                          </span>
                        )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          className="btn-blue"
                          onClick={() => toggleApprovalModal(leave)}
                        >
                          <PiHandTapFill size={20} />
                        </Button>
                      </td>
                      <td>
                        {leave?.events?.[0]?.replaced !== true ? (
                          <Button
                            size="sm"
                            color="warning"
                            className="ms-2"
                            onClick={() => openReassignModal(leave)}
                            disabled={leave?.status !== true}
                            title={
                              leave?.status !== true
                                ? "Approve the holiday first"
                                : ""
                            }
                          >
                            Reassign
                          </Button>
                        ) : (
                          <div>
                            <div
                              className="text-success fw-semibold"
                              style={{ fontSize: "12px" }}
                            >
                              ✓ Covered by
                            </div>
                            <div
                              className="fw-bold"
                              style={{ fontSize: "13px" }}
                            >
                              {employees.find(
                                e => e.id === leave?.events?.[0]?.replacedBy
                              )?.name || "—"}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="text-center">
                      No Holiday found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
            <div className="d-flex justify-content-between gap-2 flex-wrap">
              <p className="mb-0">
                Showing {paginatedLeave.length} out of {leaves.length} Leaves.
              </p>
              <div>
                <Button
                  size="sm"
                  disabled={currentPage === 1}
                  className="fs-6"
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  <FaChevronCircleLeft />
                </Button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <Button
                    key={i}
                    size="sm"
                    className="mx-2 fs-6"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </Button>
                ))}
                <Button
                  size="sm"
                  className="fs-6"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  <FaChevronCircleRight />
                </Button>
              </div>
            </div>

            {/* --- Approval Modal --- */}
            <Modal
              isOpen={modalOpen}
              toggle={() => setModalOpen(false)}
              centered
            >
              <ModalHeader toggle={() => setModalOpen(false)}>
                Holiday Details
              </ModalHeader>
              <ModalBody>
                {selectedLeave && (
                  <Table bordered responsive className="bg-white">
                    <tbody>
                      <tr>
                        <td>
                          <b>Employee</b>
                        </td>
                        <td>{selectedLeave.employee?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Applied On</b>
                        </td>
                        <td>
                          {moment(selectedLeave.createdAt).format(
                            "DD/MM/YYYY | hh:mm A"
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Date</b>
                        </td>
                        <td>
                          {moment(selectedLeave?.fromDate)?.format(
                            "DD/MM/YYYY"
                          )}{" "}
                          -{" "}
                          {moment(selectedLeave?.toDate)?.format("DD/MM/YYYY")}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Location</b>
                        </td>
                        <td>{selectedLeave?.events[0]?.location?.name}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Time</b>
                        </td>
                        <td>
                          {formatAnyTime(selectedLeave?.events[0]?.inTime)} -{" "}
                          {formatAnyTime(selectedLeave?.events[0]?.outTime)}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Total Hrs</b>
                        </td>
                        <td>
                          {calculateHours(
                            selectedLeave?.events[0]?.inTime,
                            selectedLeave?.events[0]?.outTime
                          )}{" "}
                          Hrs
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <b>Holiday Description</b>
                        </td>
                        <td>{selectedLeave.reason}</td>
                      </tr>
                      <tr>
                        <td>
                          <b>Status</b>
                        </td>
                        <td>
                          <span
                            className={`badge px-2 py-2 ${
                              selectedLeave.status === true
                                ? "bg-success"
                                : selectedLeave.status === false
                                ? "bg-danger"
                                : "bg-warning"
                            }`}
                          >
                            {selectedLeave?.status === null
                              ? "Pending"
                              : selectedLeave?.status === true
                              ? "Approved"
                              : "Rejected"}
                          </span>
                        </td>
                      </tr>
                      {selectedLeave?.events?.[0]?.replaced && (
                        <tr>
                          <td>
                            <b>Reassigned To</b>
                          </td>
                          <td>
                            {employees.find(
                              e =>
                                e.id === selectedLeave?.events?.[0]?.replacedBy
                            )?.name ||
                              `ID: ${selectedLeave?.events?.[0]?.replacedBy}`}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </ModalBody>
              {checkAccess(4, 4) && (
                <ModalFooter>
                  <Button
                    color="success"
                    onClick={() => handleApproval(selectedLeave?.id, true)}
                    disabled={
                      selectedLeave?.status === true &&
                      selectedLeave?.events?.[0]?.replaced === true
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    color="danger"
                    onClick={() => handleApproval(selectedLeave?.id, false)}
                    disabled={
                      selectedLeave?.status === true &&
                      selectedLeave?.events?.[0]?.replaced === true
                    }
                  >
                    Reject
                  </Button>
                </ModalFooter>
              )}
            </Modal>

            {/* --- Add Leave Modal --- */}
            <Modal
              isOpen={addModalOpen}
              toggle={() => {
                resetAddLeaveForm()
                setAddModalOpen(false)
              }}
              centered
              size="lg"
            >
              <ModalHeader toggle={() => {
                resetAddLeaveForm()
                setAddModalOpen(false)
              }}>
                Add Holiday Request
              </ModalHeader>
              <ModalBody style={{ maxHeight: "650px", overflowY: "auto" }}>
                <Row>
                  <FormGroup className="col-12 col-md-6">
                    <Label>
                      Employee <span className="text-danger">*</span>
                    </Label>
                    <select
                      className={`form-select ${
                        validationErrors.employeeId ? "is-invalid" : ""
                      }`}
                      value={newLeave.employeeId}
                      onChange={e =>
                        setNewLeave({ ...newLeave, employeeId: e.target.value })
                      }
                    >
                      <option value="">Select Employee</option>
                      {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                    </select>
                    {validationErrors.employeeId && (
                      <div className="invalid-feedback">
                        {validationErrors.employeeId}
                      </div>
                    )}
                  </FormGroup>

                  <FormGroup className="col-12 col-md-6">
                    <Label>
                      Holiday Dates <span className="text-danger">*</span>
                    </Label>
                    <RangePicker
                      value={leavedateRange}
                      onChange={dates => {
                        setLeaveDateRange(dates)
                        setNewLeave(prev => ({
                          ...prev,
                          fromDate: dates?.[0] || null,
                          toDate: dates?.[1] || null,
                        }))
                      }}
                      format="DD/MM/YYYY"
                      allowClear
                      className={`w-100 ${
                        validationErrors.dateRange ? "border-danger" : ""
                      }`}
                      style={{
                        height: "38px",
                        borderRadius: "6px",
                        border: validationErrors.dateRange
                          ? "1px solid #dc3545"
                          : "",
                      }}
                    />
                    {validationErrors.dateRange && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.dateRange}
                      </small>
                    )}
                  </FormGroup>
                </Row>

                {displayBalance && (
                  <div
                    className={`alert d-flex justify-content-between align-items-center mt-3 mb-0 ${
                      displayBalance.displayRemaining <= 0
                        ? "alert-danger"
                        : displayBalance.displayRemaining <= 1
                        ? "alert-warning"
                        : "alert-info"
                    }`}
                    style={{ borderRadius: "8px" }}
                  >
                    <span>
                      <strong>Leave Balance</strong> — {displayBalance.locationName}
                    </span>
                    <span>
                      <span className="badge bg-secondary me-2">
                        Total: {displayBalance.totalLeaves}
                      </span>
                      <span className="badge bg-primary me-2">
                        Paid: {displayBalance.displayPaid}
                      </span>
                      {displayBalance.displayExtra > 0 && (
                        <span className="badge bg-warning text-dark me-2">
                          Extra: {displayBalance.displayExtra}
                        </span>
                      )}
                      <span
                        className={`badge ${
                          displayBalance.displayRemaining <= 0
                            ? "bg-danger"
                            : "bg-success"
                        }`}
                      >
                        Remaining: {displayBalance.displayRemaining}
                      </span>
                    </span>
                  </div>
                )}

                <FormGroup className="mt-3">
                  <Label>
                    Affected Shifts/Locations{" "}
                    {jobs.length > 0 && <span className="text-danger">*</span>}
                  </Label>
                  <Select
                    value={newLeave.calendarSlotIds}
                    onChange={selected => {
                      setNewLeave(prev => ({
                        ...prev,
                        calendarSlotIds: selected || [],
                      }))
                    }}
                    options={jobs.map(job => ({
                      value: job.id,
                      label: `${job.location?.name} (${formatAnyTime(
                        job.inTime
                      )} - ${formatAnyTime(job.outTime)})`,
                      data: job,
                    }))}
                    isMulti
                    placeholder={
                      jobs.length > 0
                        ? "Select shifts to cover with this holiday..."
                        : "No scheduled shifts found for selected dates"
                    }
                    isDisabled={jobs.length === 0}
                    styles={{
                      ...customStyles,
                      control: (base, state) => ({
                        ...base,
                        borderColor: validationErrors.calendarSlotIds
                          ? "#dc3545"
                          : base.borderColor,
                        boxShadow: validationErrors.calendarSlotIds
                          ? "0 0 0 0.2rem rgba(220,53,69,.25)"
                          : base.boxShadow,
                      }),
                    }}
                  />
                  {jobs.length > 0 ? (
                    validationErrors.calendarSlotIds && (
                      <small className="text-danger d-block mt-1">
                        {validationErrors.calendarSlotIds}
                      </small>
                    )
                  ) : (
                    <small className="text-muted d-block mt-1">
                      Employee has no shifts scheduled — leave can still be
                      applied as a free day.
                    </small>
                  )}
                </FormGroup>

                {newLeave.calendarSlotIds.length > 0 && (
                  <div className="mt-3 p-3 border rounded bg-light">
                    <h6 className="mb-3 text-primary">Selected Shifts</h6>
                    <Table size="sm" bordered>
                      <thead className="table-light">
                        <tr>
                          <th>Location</th>
                          <th>In Time</th>
                          <th>Out Time</th>
                          <th>Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        {newLeave.calendarSlotIds.map((item, idx) => {
                          const job = item.data
                          const baseRemaining = leaveBalanceInfo
                            ? Number(leaveBalanceInfo.remainingLeaves) || 0
                            : 0
                          const isExtra = idx >= baseRemaining
                          return (
                            <tr key={item.value}>
                              <td>
                                {job.location?.name}
                                {isExtra ? (
                                  <span className="badge bg-warning text-dark ms-2">
                                    Extra
                                  </span>
                                ) : (
                                  <span className="badge bg-success ms-2">
                                    Paid
                                  </span>
                                )}
                              </td>
                              <td>{formatAnyTime(job.inTime)}</td>
                              <td>{formatAnyTime(job.outTime)}</td>
                              <td>
                                {calculateHours(job.inTime, job.outTime)} hrs
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}

                <FormGroup className="mt-3">
                  <Label>
                    Reason / Description <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    rows="4"
                    value={newLeave.reason}
                    onChange={e =>
                      setNewLeave({ ...newLeave, reason: e.target.value })
                    }
                    placeholder="e.g., Annual leave, Sick leave, Family emergency..."
                    invalid={!!validationErrors.reason}
                  />
                  {validationErrors.reason && (
                    <div className="invalid-feedback">
                      {validationErrors.reason}
                    </div>
                  )}
                </FormGroup>

                <div className="text-end mt-4">
                  <Button
                    color="secondary"
                    className="me-2"
                    onClick={() => {
                      resetAddLeaveForm()
                      setAddModalOpen(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button className="btn-blue" onClick={handleAddLeave}>
                    Submit Holiday Request
                  </Button>
                </div>
              </ModalBody>
            </Modal>

            <Modal
              isOpen={reassignModalOpen}
              toggle={() => setReassignModalOpen(false)}
              centered
            >
              <ModalHeader toggle={() => setReassignModalOpen(false)}>
                Reassign Holiday
              </ModalHeader>

              <ModalBody>
                {reassignLeave && (
                  <Table bordered>
                    <tbody>
                      <tr>
                        <td>
                          <b>Current Employee</b>
                        </td>
                        <td>{reassignLeave.employee?.name}</td>
                      </tr>

                      <tr>
                        <td>
                          <b>Holiday Dates</b>
                        </td>
                        <td>
                          {moment(reassignLeave.fromDate).format("DD/MM/YYYY")}{" "}
                          - {moment(reassignLeave.toDate).format("DD/MM/YYYY")}
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <b>Shift</b>
                        </td>
                        <td>
                          {reassignLeave?.events?.[0]?.location?.name} <br />
                          {formatAnyTime(
                            reassignLeave?.events?.[0]?.inTime
                          )} -{" "}
                          {formatAnyTime(reassignLeave?.events?.[0]?.outTime)}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                )}

                <FormGroup>
                  <Label>
                    Assign To <span className="text-danger">*</span>
                  </Label>
                  <select
                    className="form-select"
                    value={newEmployeeId}
                    onChange={e => setNewEmployeeId(e.target.value)}
                  >
                    <option value="">Select Employee</option>
                    {releatedEmployees
                      .filter(emp => emp.id !== reassignLeave?.employeeId)
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                </FormGroup>
              </ModalBody>

              <ModalFooter>
                <Button
                  color="secondary"
                  onClick={() => setReassignModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button color="primary" onClick={handleReassign}>
                  Reassign
                </Button>
              </ModalFooter>
            </Modal>
          </CardBody>
        </Card>
      </PermittedComponent>
    </div>
  )
}

const customStyles = {
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? "#007BFF" : isFocused ? "#F1F1F1" : "white",
    color: isSelected ? "white" : "#333",
    padding: "10px 8px",
    cursor: "pointer",
    zIndex: "1000",
  }),
  menu: styles => ({
    ...styles,
    backgroundColor: "#fff",
    borderRadius: "4px",
    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
  }),
}

export default Leaves
